import React from 'react';
import { ChevronUp, Clock3 } from 'lucide-react';
import { MeterRealtimeMetric, MeterRealtimeMetrics } from '../../hooks/useMeterRealtimeMetrics';

interface RealTimeMetricsPanelProps {
  metrics: MeterRealtimeMetrics;
}

const statusLabel: Record<'ok' | 'warning' | 'unknown', string> = {
  ok: 'OK',
  warning: 'Alerte',
  unknown: 'N/A',
};

const buildSparklinePath = (values: number[], width = 160, height = 34): string => {
  if (values.length < 2) return '';
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 1e-6);

  return values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
};

const buildAreaPath = (linePath: string, width = 160, height = 34): string => {
  if (!linePath) return '';
  return `${linePath} L ${width} ${height} L 0 ${height} Z`;
};

const MetricCard: React.FC<{ metric: MeterRealtimeMetric }> = ({ metric }) => {
  const linePath = buildSparklinePath(metric.trend);
  const areaPath = buildAreaPath(linePath);
  const chartColor = metric.status === 'warning' ? '#f59e0b' : metric.status === 'unknown' ? '#94a3b8' : '#36a43d';

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
      <div className="flex items-center justify-between">
        <div className="text-slate-800 text-[15px] font-medium">
          {metric.title}
        </div>
        <div className="flex items-center gap-1.5 text-slate-800">
          <span className={`inline-block h-3 w-3 rounded-full ${metric.status === 'warning' ? 'bg-amber-500' : metric.status === 'ok' ? 'bg-green-500' : 'bg-slate-400'}`} />
          <span className="text-[12px] font-semibold">{statusLabel[metric.status]}</span>
        </div>
      </div>

      <div className="mt-2">
        <span className="text-[28px] leading-none font-bold text-blue-600">
          {metric.formattedValue} {metric.unit}
        </span>
      </div>

      <div className="mt-2 rounded-lg border border-slate-200/80 bg-white px-2.5 py-2">
        <div className="h-12">
          {linePath ? (
            <svg viewBox="0 0 160 34" preserveAspectRatio="none" className="w-full h-full">
              <path d={areaPath} fill={chartColor} opacity="0.10" />
              <path d={linePath} fill="none" stroke={chartColor} strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          ) : (
            <div className="h-full flex items-center justify-center text-[10px] text-slate-400">Pas de tendance</div>
          )}
        </div>
      </div>
    </div>
  );
};

export const RealTimeMetricsPanel: React.FC<RealTimeMetricsPanelProps> = ({ metrics }) => {
  if (!metrics.isMeterSelected) return null;

  return (
    <div className="space-y-3">
      <div className="px-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-900">
            <Clock3 className="h-4 w-4 text-slate-500" />
            <h4 className="text-xs font-semibold">Métriques en temps réel</h4>
          </div>
          <ChevronUp className="h-4 w-4 text-slate-500" />
        </div>
        {!metrics.hasDeviceName && (
          <p className="mt-2 text-xs text-amber-700">
            Numéro du compteur introuvable. Impossible de charger les métriques API.
          </p>
        )}
        {metrics.hasDeviceName && metrics.isLoading && (
          <p className="mt-2 text-xs text-slate-500">Chargement des métriques...</p>
        )}
        {metrics.hasDeviceName && metrics.isError && (
          <p className="mt-2 text-xs text-amber-700">Certaines métriques ne sont pas disponibles actuellement.</p>
        )}
      </div>

      <MetricCard metric={metrics.voltage} />
      <MetricCard metric={metrics.consumption} />
      <MetricCard metric={metrics.power} />
    </div>
  );
};
